import React from "react";
import { createPortal } from "react-dom";
import { Calendar, TrendingUp, DollarSign, BarChart3, X } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./TradeDashboard.scss";
import TradeTable from "../TradeTable";
import TradingControl from "../TradingControl";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useTradeDashboard } from "./useTradeDashboard";

const TradeDashboard: React.FC = () => {
  const {
    trades,
    summary,
    loading,
    error,
    dateRange,
    setDateRange,
    isCalendarModalOpen,
    setIsCalendarModalOpen,
    fetchTrades,
    formatCurrency,
    formatPercentage,
    applyPresetLast7Days,
    applyPresetLast30Days,
    applyPresetLastMonth,
    applyCustomRangeIfComplete,
  } = useTradeDashboard();

  if (error) {
    return (
      <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
        <EosBackdrop />
        <Header />
        <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-8 px-4 sm:px-6 lg:px-8">
          <div className="glass-card text-center py-12">
            <div className="mx-auto h-16 w-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-red-300" />
            </div>
            <h3 className="text-xl font-semibold text-slate-100 mb-2">Failed to Load Trades</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">{error}</p>
            <button
              onClick={() => fetchTrades(dateRange.startDate, dateRange.endDate)}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ink-900 focus:ring-emerald-500 transition-colors duration-200"
            >
              Retry
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden text-slate-100">
      <EosBackdrop />
      <Header />

      <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <TradingControl />

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass-card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BarChart3 className="h-8 w-8 text-emerald-300" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-500">Total Trades</p>
                    <p className="text-2xl font-bold text-slate-100">{summary.total_trades || 0}</p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-8 w-8 text-green-300" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-500">Total P&L</p>
                    <p
                      className={`text-2xl font-bold ${
                        (summary.total_pnl || 0) >= 0 ? "text-green-300" : "text-red-300"
                      }`}
                    >
                      {formatCurrency(summary.total_pnl || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <DollarSign className="h-8 w-8 text-accent-300" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-500">Success Rate</p>
                    <p className="text-2xl font-bold text-slate-100">{formatPercentage(summary.success_rate || 0)}</p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BarChart3 className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-500">Avg P&L</p>
                    <p
                      className={`text-2xl font-bold ${
                        (summary.avg_pnl || 0) >= 0 ? "text-green-300" : "text-red-300"
                      }`}
                    >
                      {formatCurrency(summary.avg_pnl || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="glass-card overflow-hidden">
            <div className="p-6">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <h3 className="text-lg font-medium text-slate-100">Executed Trades</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={applyPresetLast7Days}
                      className="whitespace-nowrap text-xs px-3 py-1 rounded-full bg-white/10 text-slate-300 hover:bg-white/15 transition-colors"
                    >
                      Last 7 days
                    </button>
                    <button
                      onClick={applyPresetLast30Days}
                      className="whitespace-nowrap text-xs px-3 py-1 rounded-full bg-white/10 text-slate-300 hover:bg-white/15 transition-colors"
                    >
                      Last 30 days
                    </button>
                    <button
                      onClick={applyPresetLastMonth}
                      className="whitespace-nowrap text-xs px-3 py-1 rounded-full bg-white/10 text-slate-300 hover:bg-white/15 transition-colors"
                    >
                      Last month
                    </button>
                    <button
                      onClick={() => setIsCalendarModalOpen(true)}
                      className="whitespace-nowrap text-xs px-3 py-1 rounded-full bg-white/10 text-slate-300 hover:bg-white/15 transition-colors flex items-center space-x-1"
                    >
                      <Calendar className="h-3 w-3" />
                      <span>Custom Range</span>
                    </button>
                  </div>
                </div>

                {!loading && <div className="text-sm text-slate-500">Showing {trades?.length || 0} trades</div>}
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
                  <span className="ml-3 text-slate-400">Loading trades...</span>
                </div>
              ) : (
                <TradeTable trades={trades} />
              )}
            </div>
          </div>
        </div>
      </div>

      {isCalendarModalOpen &&
        createPortal(
          <div
            className="fixed top-0 left-0 right-0 bottom-0 bg-black/60 flex items-center justify-center z-[9999]"
            style={{ margin: 0, padding: 0 }}
          >
            <div className="bg-ink-900 border border-white/10 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-slate-100">Select Date Range</h3>
                <button onClick={() => setIsCalendarModalOpen(false)} className="text-slate-500 hover:text-slate-300">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                  <DatePicker
                    selected={dateRange.startDate}
                    onChange={(date) => setDateRange({ ...dateRange, startDate: date })}
                    selectsStart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    maxDate={dateRange.endDate || new Date()}
                    className="w-full px-3 py-2 rounded-md border border-white/15 bg-white/5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholderText="Select start date"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">End Date</label>
                  <DatePicker
                    selected={dateRange.endDate}
                    onChange={(date) => setDateRange({ ...dateRange, endDate: date })}
                    selectsEnd
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={dateRange.startDate}
                    maxDate={new Date()}
                    className="w-full px-3 py-2 rounded-md border border-white/15 bg-white/5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholderText="Select end date"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setIsCalendarModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-300 bg-white/10 hover:bg-white/15 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCustomRangeIfComplete}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors"
                  disabled={!dateRange.startDate || !dateRange.endDate}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <Footer />
    </div>
  );
};

export default TradeDashboard;
